// The row under the board, and the lines of moves in the sheet.
//
// While a puzzle is played the row says what is happening. Once it is over
// the row holds the line's own controls: back and on through it, Continue
// the line (ask the engine to go on from here), and Back to the line (put a
// variation aside). They are there from the moment the line is, each in its
// own place and one width, so pressing one again and again is pressing the
// same spot.

import type { Puzzle, Snapshot, Step } from "../board/puzzle.ts";
import { Icon } from "../app/icons.tsx";

export function LineRow({ puzzle, snapshot }: { puzzle: Puzzle; snapshot: Snapshot }) {
  const replay = snapshot.replay;
  if (!replay) {
    return (
      <>
        <span className={`board-status tone-${snapshot.status.tone}`} role="status">
          {snapshot.status.text}
        </span>
        <span className="board-info">{snapshot.info}</span>
      </>
    );
  }
  const explore = snapshot.explore;
  const hasVariation = Boolean(explore && explore.steps.length);
  // With no move of yours in it the engine is going on with the line, and
  // the button keeps the name it had when it was first pressed.
  const askLabel = !explore || explore.engine ? "Continue" : "Engine's move";
  return (
    <>
      <button type="button" className="btn step-btn line-back" aria-label="Back a move" disabled={!replay.canBack} onClick={() => puzzle.stepBy(-1)}>
        <Icon name="prev" />
      </button>
      <button type="button" className="btn step-btn line-on" aria-label="On a move" disabled={!replay.canOn} onClick={() => puzzle.stepBy(1)}>
        <Icon name="next" />
      </button>
      {explore ? (
        <>
          <button
            type="button"
            className="btn explore-ask"
            title="Ask the engine to go on from the position on the board"
            disabled={!explore.canAsk}
            onClick={() => puzzle.askEngine()}
          >
            {explore.thinking ? "Thinking…" : askLabel}
          </button>
          <button
            type="button"
            className="btn explore-leave"
            title="Put this variation aside and go back to the line"
            data-spent={String(!hasVariation)}
            disabled={!hasVariation}
            onClick={() => puzzle.leaveExplore()}
          >
            Back to line
          </button>
        </>
      ) : null}
    </>
  );
}

/** The line as it ended, and any variation off it, a move to press each. */
export function LineMoves({ puzzle, snapshot }: { puzzle: Puzzle; snapshot: Snapshot }) {
  const replay = snapshot.replay;
  if (!replay) return null;
  const explore = snapshot.explore;
  return (
    <>
      <div className="moves replay">
        {replay.steps.slice(1).map((step, i) => (
          <StepButton key={i} step={step} at={!replay.along && replay.at === i + 1} onClick={() => puzzle.gotoStep(i + 1)} />
        ))}
      </div>
      {explore && explore.steps.length ? (
        <div className="moves explore">
          <span className="moves-label">{explore.engine ? "The engine goes on:" : "You tried:"}</span>
          {explore.steps.map((step, i) => (
            <StepButton key={i} step={step} at={explore.at === i + 1} onClick={() => puzzle.gotoVariation(i + 1)} />
          ))}
        </div>
      ) : null}
      {explore ? (
        <p className="explore-note">{explore.note || (explore.steps.length ? "" : "Or play any move here, either side, and ask the engine about that.")}</p>
      ) : null}
    </>
  );
}

function StepButton({ step, at, onClick }: { step: Step; at: boolean; onClick: () => void }) {
  return (
    <button type="button" className={"step" + (step.yours ? " yours" : "") + (at ? " at" : "")} onClick={onClick}>
      {step.san}
    </button>
  );
}

/** What the line came to, and the two ways it can go on. */
export function Aftermath({ puzzle, snapshot }: { puzzle: Puzzle; snapshot: Snapshot }) {
  const after = snapshot.aftermath;
  const cont = snapshot.continuations;
  return (
    <>
      {after && after.good ? (
        <p className="aftermath good">
          <b>{after.score}</b> {after.text}
        </p>
      ) : null}
      {cont ? (
        <div className="continuations">
          <span>Then:</span>
          <button type="button" className="pick" aria-pressed={cont.which === "best"} onClick={() => puzzle.pickContinuation("best")}>
            {cont.best}
          </button>
          <button type="button" className="pick" aria-pressed={cont.which === "common"} onClick={() => puzzle.pickContinuation("common")}>
            {cont.common}
          </button>
        </div>
      ) : null}
    </>
  );
}
